import { Outlet, createFileRoute, redirect, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";

import { RegulationHeader } from "../components/regulation/RegulationHeader";
import { RegulationSearchProvider } from "../components/regulation/regulationSearch";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { regulationSectionLabel } from "../components/regulation/RegulationSidebarNav";
import { SidebarInset } from "../components/ui/sidebar";
import { recordVisitedOrg, useActiveOrgId, useOrgs } from "../state/orgs";
import { useReviewCountNotifications } from "../state/useReviewCountNotifications";
import { isElectron } from "../env";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

/**
 * The tenant-scoped Compliance tree — `/$org/compliance/$section`, matching
 * `$org.settings`. The org segment keeps the selected tenant visible and
 * bookmarkable, which is what makes these pages resolve to the right
 * organization instead of inferring one.
 *
 * The topbar IS the compliance header: breadcrumb, search and Probe
 * live in the same row every other space uses for its title, so this space
 * has one header, not a title bar with a second bar under it. Search state
 * lives here and reaches sections through RegulationSearchProvider.
 */
function RegulationContentLayout() {
  const { org: orgSegment } = Route.useParams();
  const pathname = useLocation({ select: (location) => location.pathname });
  const activeOrgId = useActiveOrgId();
  const { orgs } = useOrgs();
  const { org } = useRegulationOrg();
  useReviewCountNotifications(org?.id ?? null);

  // Visiting an org's regulation pages makes it the active tenant, so `/` and
  // the legacy redirector agree with the URL. Recorded only once the segment
  // resolves against the directory — the raw segment may be a slug.
  useEffect(() => {
    if (activeOrgId && orgs.some((org) => org.id === activeOrgId)) {
      recordVisitedOrg(activeOrgId);
    }
  }, [activeOrgId, orgs]);

  // The findings, record-update and monitor pages carry their own one-row header
  // (title, tabs, filters, actions), so the breadcrumb strip would be a second.
  const ownHeader = hasOwnPageHeader(pathname, orgSegment);
  const header = (
    <RegulationHeader section={regulationSectionLabel(pathname, orgSegment)} />
  );

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        {!isElectron && !ownHeader && (
          <header
            className={cn(
              "workspace-topbar gap-3 px-3 transition-[padding-left] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) motion-reduce:transition-none sm:px-5",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            {header}
          </header>
        )}

        {isElectron && (
          <div
            className={cn(
              "drag-region flex h-[52px] shrink-0 items-center gap-3 px-5 transition-[padding-left] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) motion-reduce:transition-none wco:h-[env(titlebar-area-height)] wco:pr-[calc(100vw-env(titlebar-area-width)-env(titlebar-area-x)+1em)]",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            {header}
          </div>
        )}

        {/* Sections keep their own inline filters, so the shared query is
            always empty here. */}
        <RegulationSearchProvider value="">
          <Outlet />
        </RegulationSearchProvider>

      </div>
    </SidebarInset>
  );
}

/** Pages that draw their own header row instead of the breadcrumb strip. */
export function hasOwnPageHeader(pathname: string, org: string): boolean {
  const path = pathname.replace(/\/$/, "");
  const base = `/${org}/compliance`;
  return (
    path === `${base}/findings` ||
    path.startsWith(`${base}/findings/`) ||
    path === `${base}/suggestions` ||
    path === `${base}/regulatory`
  );
}

export const Route = createFileRoute("/$org/compliance")({
  beforeLoad: async ({ context }) => {
    if (
      context.authGateState.status !== "authenticated" &&
      context.authGateState.status !== "hosted-static"
    ) {
      throw redirect({ to: "/pair", replace: true });
    }

    // /$org/compliance is a real page now (the Overview); the old redirect to
    // /policies predates it and would keep the landing page unreachable.
  },
  component: RegulationContentLayout,
});
